import React, {useState, useEffect, lazy, Suspense, useMemo} from "react"
import {useNavigate} from "react-router-dom"
import {useCustomer} from "../../../common/contexts/CustomerContext"
import {useGetParentRequirementDashboard} from "../api/queries"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import {useUserPreferences} from "../../../common/contexts/UserPreferencesContext"
import {BarCustomLayerProps, BarDatum} from "@nivo/bar"
import {CartesianMarkerProps} from "@nivo/core"
import Pagination from "@murphy-frontend/web-core/components/Pagination"
import Button from "@murphy-frontend/web-core/components/Button"
import {useModal} from "@murphy-frontend/web-core/contexts/ModalContext"
import {ModalConfiguration} from "@murphy-frontend/web-core/components/Modal"
import CategoryChart from "./CategoryChart"
import DropdownSearch from "@murphy-frontend/web-core/components/DropdownSearch"
import {FilterType} from "@murphy-frontend/web-core/components/FilterControl"
import FiltersParent from "./FiltersParent"
import SearchBar from "@murphy-frontend/web-core/components/SearchBar"
import FilterToggleRow from "../../UsersAdmin/components/FilterToggleRow"

const ResponsiveBar = lazy(() => import("@nivo/bar").then(module => ({default: module.ResponsiveBar})))

type OptionType = {
	value: string
	label: string
}

export type BarType = {
	customerId: string
	customerName: string
	value: number
}

export interface RequirementDashboard {
	GoalLevel: number
	categoryList: categoryList[]
	averageScore: number
}

export interface categoryList {
	categoryId: number
	value: number
}

const PAGE_SIZE = 5
const CEILING = 4
const OPTIONS = [
	{
		value: "DESC",
		label: "Top 5"
	},
	{
		value: "ASC",
		label: "Bottom 5"
	}
]

const ParentDashboard = () => {
	const {translations, showNonZero, setShowNonZero} = useUserPreferences()
	const [chartData, setChartData] = useState<BarType[]>([])
	const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>()
	const [sortValue, setSortValue] = useState(null)
	const [customerList, setCustomerList] = useState<OptionType[]>([])
	const [filters, setFilters] = useState<FilterType>({
		displayName: "Don't show zero",
		columnname: "nonzero",
		filtervalue: 0,
		isActive: false
	})
	const [currentSearchText, setCurrentSearchText] = useState("")

	const [currentPage, setCurrentPage] = useState(1)
	const [tableRows, setTableRows] = useState([])

	const {openModal, closeModal} = useModal()

	const {customer} = useCustomer()

	const {data: dashboard, isLoading, isPending} = useGetParentRequirementDashboard(customer.Id)

	const navigate = useNavigate()

	useEffect(() => {
		setFilters(generateInitialFilters())
	}, [translations, showNonZero])

	const generateInitialFilters = () => {
		const nonZeroCheckbox = {
			displayName: "Don't show zero",
			columnname: "nonzero",
			filtervalue: 0,
			isActive: showNonZero
		}

		return nonZeroCheckbox
	}

	const onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value
		setCurrentSearchText(newValue)
	}

	useEffect(() => {
		let data: BarType[] = (dashboard || []).map(child => ({
			customerId: child.CustomerId,
			customerName: child.CustomerName,
			value: child.Avg
		}))
		let customerData: OptionType[] = (dashboard || []).map(_ => ({
			label: _.CustomerName,
			value: _.CustomerId
		}))

		if (!!currentSearchText.trim()) {
			data = data.filter(_ => _.customerName.toUpperCase().startsWith(currentSearchText.toUpperCase()))
		}

		switch (sortValue) {
			case "ASC":
				data = data.sort((a, b) => a.value - b.value)
				break
			case "DESC":
				data = data.sort((a, b) => b.value - a.value)
				break
			case "NOZERO":
				data = data.filter(_ => _.value !== 0)
				break
			default:
				data = data.sort((a, b) => a.customerName.localeCompare(b.customerName))
				break
		}

		if (showNonZero === true) {
			data = data.filter(_ => !!_.value)
		}

		setChartData(data)
		setTableRows(data)
		setCustomerList(customerData)
		setCurrentPage(1)
	}, [dashboard, sortValue, currentSearchText, showNonZero])

	const currentTableData = useMemo(() => {
		const firstPageIndex = (currentPage - 1) * PAGE_SIZE
		const lastPageIndex = firstPageIndex + PAGE_SIZE
		return tableRows.slice(firstPageIndex, lastPageIndex)
	}, [currentPage, tableRows])

	if (isLoading || isPending) {
		return (
			<div className='spinner-container-global-center'>
				<Spinner />
			</div>
		)
	}

	const licenseAvg = currentTableData.reduce((acc, child) => child.value + acc, 0) / currentTableData.length
	const markers: CartesianMarkerProps[] = [
		{
			axis: "y",
			value: licenseAvg,
			lineStyle: {stroke: "#B0B0B0", strokeWidth: 1},
			legend: `${translations.tmsAvg || "Avg"} (${licenseAvg?.toFixed(2) ?? ""})`,
			legendPosition: "top-right",
			legendOrientation: "horizontal"
		}
	]

	const CategoryLayer = (props: BarCustomLayerProps<BarDatum>): JSX.Element => {
		const bars = props.bars

		return (
			<>
				{bars.map(bar => {
					const customerId = bar.data.data.customerId
					const handleExploreClick = () => {
						navigate(`/tmsStartpage/${customerId}`)
					}

					const customerName = bar.data.data.customerName.toString()

					return (
						<foreignObject
							key={bar.key}
							x={bar.x}
							y={bar.y + bar.height + 5}
							width={bar.width}
							height={100}
							style={{overflow: "visible"}}
						>
							<div className='category-layer'>
								<div className='graph-customer-name'>
									{customerName.length > 14 ? (
										<span title={customerName}>{customerName.slice(0, 10) + "..."}</span>
									) : (
										customerName
									)}
								</div>
								<button className='explore-button' onClick={handleExploreClick}>
									Explore
								</button>
							</div>
						</foreignObject>
					)
				})}
			</>
		)
	}

	const TopColorLayer = (props: BarCustomLayerProps<BarDatum>): JSX.Element => {
		const bars = props.bars
		const TOP_RECT_HEIGHT = 4
		return (
			<>
				{bars.map(bar => {
					const rectHeight = Math.min(bar.height, TOP_RECT_HEIGHT)
					return (
						<rect
							key={bar.key + "-top"}
							x={bar.x}
							y={bar.y}
							width={bar.width}
							height={rectHeight}
							fill={"#AF52DE"}
						/>
					)
				})}
			</>
		)
	}

	let gridYValues = [0, 1, 2, 3, 4]

	const handleChange = (val: string) => {
		setSortValue(val)
	}

	const syncNonZeroFilter = (filter: FilterType) => {
		console.log(filter)
		const nonZero = filter.isActive
		setShowNonZero(nonZero)
	}

	const resetDashboardFilters = () => {
		setFilters({...filters, isActive: false})
		setCurrentSearchText("")
		setSortValue(null)
		syncNonZeroFilter({...filters, isActive: false})
	}

	const onToggleFilter = (filt: FilterType[]) => {
		let updatedFilter = filt.find(_ => _.columnname === "nonzero")
		setFilters(updatedFilter)
		syncNonZeroFilter(updatedFilter)
	}

	const onClickFilter = () => {
		const filtersParentConfiguartion: ModalConfiguration = {
			body: (
				<FiltersParent
					allFilters={[filters]}
					closeModal={closeModal}
					toggleFilter={onToggleFilter}
					reset={resetDashboardFilters}
					translations={translations}
				/>
			),
			buttonText: "Ok",
			hideButton: true
		}
		openModal(filtersParentConfiguartion)
	}

	return (
		<>
			<section className='search-wrapper'>
				<SearchBar
					val={currentSearchText}
					handleChange={onSearchChanged}
					customClasses={["search-bar-full-width"]}
					placeholder={translations.search}
					showSearchIcon={true}
				/>
				<Button isDarkButton={true} onClick={() => onClickFilter()}>
					{translations["lang-filter"]}
				</Button>
			</section>
			<section className='filters-container-general'>
				<>
					{filters.isActive && (
						<FilterToggleRow
							filter={filters}
							onToggleFilter={resetDashboardFilters}
							key={filters.displayName}
							filterName="Don't Show Zero"
						/>
					)}
				</>
			</section>
			<section className='card-container margin-top-20'>
				<div
					className='parent-dashboard'
					style={{
						width: "100%", // Ensure full width
						maxWidth: "1200px", // Prevent overriding parent maxWidth
						margin: "0 auto", // Center horizontally
						padding: "20px", // Match the original padding from tmsAssessmentResult
						boxSizing: "border-box" // Include padding in width/height calculations
					}}
				>
					<div className='dashboard__title-container'>
						<h2>{translations["crisis-management"]}</h2>
					</div>
					<div className='dashboard-dropdown-wrap'>
						<div className='dashboard-dropdown-text'>{`Showing ${currentTableData.length} of ${chartData.length} departments:`}</div>
						<DropdownSearch
							options={OPTIONS}
							id={"parent"}
							selectedVal={sortValue}
							handleChange={handleChange}
							label={"label"}
						/>
					</div>
					<div className='dashboard__chart-container'>
						<Suspense fallback={<Spinner />}>
							<ResponsiveBar
								data={currentTableData}
								keys={["value"]}
								indexBy='customerName'
								margin={{top: 40, right: 40, bottom: 160, left: 40}}
								padding={0.6}
								valueScale={{type: "linear", max: CEILING}}
								indexScale={{type: "band", round: true}}
								enableLabel={false}
								colors={"#F6F6F5"}
								borderWidth={0}
								borderColor='none'
								theme={{
									background: "#fff",
									axis: {
										domain: {line: {stroke: "#ccc", strokeWidth: 1.5}},
										ticks: {line: {stroke: "#ccc", strokeWidth: 1.5}}
									},
									grid: {line: {stroke: "#eee", strokeWidth: 1}}
								}}
								axisBottom={null}
								gridYValues={gridYValues}
								axisLeft={{
									tickValues: gridYValues,
									tickSize: 0,
									tickPadding: 10,
									tickRotation: 0,
									legend: "",
									legendPosition: "middle",
									legendOffset: -40,
									format: value => value
								}}
								markers={markers}
								layers={["grid", "axes", "bars", "markers", TopColorLayer, "legends", CategoryLayer]}
							/>
						</Suspense>
					</div>
					<Pagination
						className='pagination-bar'
						currentPage={currentPage}
						totalCount={tableRows.length}
						pageSize={PAGE_SIZE}
						onPageChange={page => setCurrentPage(page)}
					/>
				</div>
			</section>
			<section className='card-container margin-top-20'>
				<div
					className='parent-dashboard'
					style={{
						width: "100%", // Ensure full width
						maxWidth: "1200px", // Prevent overriding parent maxWidth
						margin: "0 auto", // Center horizontally
						padding: "20px", // Match the original padding from tmsAssessmentResult
						boxSizing: "border-box" // Include padding in width/height calculations
					}}
				>
					<div className='dashboard__title-container'>
						<h2>{translations["view-by-departmnet"]}</h2>
					</div>
					<div className='header-right-btn level-goal-btn'>
						<DropdownSearch
							options={customerList}
							id={"customers"}
							selectedVal={selectedCustomerId}
							handleChange={val => setSelectedCustomerId(val)}
							label={"label"}
						/>
					</div>
					{selectedCustomerId && (
						<CategoryChart
							customerId={selectedCustomerId}
							showExploreButton={false}
							showRecommendedButton={false}
						/>
					)}
				</div>
			</section>
		</>
	)
}

export default ParentDashboard
